import type { VariantProps } from 'class-variance-authority';
import type React from 'react';
import { cn } from '@savvy-kit/lib/utils';
import { cva } from 'class-variance-authority';
import { isNil } from 'es-toolkit';
import { X, Zap } from 'lucide-react';

import { Badge } from '../../components/badge/badge';

// ============================================================================
// Variants
// ============================================================================

const PRESET_COLORS = {
  green: 'bg-primary',
  red: 'bg-destructive',
  blue: 'bg-cyan-300',
  yellow: 'bg-amber-400',
  purple: 'bg-savvy-lavender',
  stone: 'bg-savvy-gold',
  orange: 'bg-orange-600',
  maroon: 'bg-savvy-maroon',
} as const;

// Mirrors PRESET_COLORS but for the Smart Tag indicator (Zap icon). The icon
// renders inside a transparent box and inherits the category color through
// text/fill — bg-* doesn't work for an SVG, so we need the parallel map.
const PRESET_ICON_COLORS = {
  green: 'text-primary fill-primary',
  red: 'text-destructive fill-destructive',
  blue: 'text-cyan-300 fill-cyan-300',
  yellow: 'text-amber-400 fill-amber-400',
  purple: 'text-savvy-lavender fill-savvy-lavender',
  stone: 'text-savvy-gold fill-savvy-gold',
  orange: 'text-orange-600 fill-orange-600',
  maroon: 'text-savvy-maroon fill-savvy-maroon',
} as const;

const indicatorVariants = cva('size-2 rounded-full mx-0.5', {
  variants: {
    color: PRESET_COLORS,
  },
});

// Badge defaults to `h-6` + `overflow-hidden`, which clips glyph descenders once we
// tighten line-height for alignment. Let height follow content while keeping a compact
// minimum, and avoid clipping the label.
const tagVariants = cva(
  'font-normal text-xs leading-tight cursor-default normal-case h-auto min-h-6 overflow-visible rounded-md bg-slate-900/5',
  {
    variants: {
      variant: {
        filter: 'hover:bg-component-hover focus:ring-border-light focus:ring-1 ring-offset-2',
        selectable:
          'hover:bg-component-hover focus:ring-border-light focus:ring-1 ring-offset-2 active:text-primary active:border-primary',
        operational: 'hover:bg-component-hover focus:ring-border-light focus:ring-1 ring-offset-2',
        'read-only': 'border-transparent',
        interactive:
          'ring-offset-2 hover:bg-component-hover focus:ring-1 focus:ring-border-light active:ring-primary/50',
      },
    },
    defaultVariants: {
      variant: 'read-only',
    },
  },
);

type IndicatorColor = NonNullable<VariantProps<typeof indicatorVariants>['color']>;

function isPresetColor(color: string): color is IndicatorColor {
  return color in PRESET_COLORS;
}

// ============================================================================
// TagIndicator
// ============================================================================

function TagIndicator({
  className,
  color,
  emoji,
  smart,
  style,
  ...props
}: Omit<React.ComponentProps<'span'>, 'color'> & {
  readonly color?: IndicatorColor | string;
  readonly emoji?: string;
  readonly smart?: boolean;
}) {
  const isPreset = typeof color === 'string' && color !== '' && isPresetColor(color);

  // Smart wins over color/emoji: a smart tag must show its Zap icon on every
  // surface, regardless of whether a category color was supplied. (Dropdowns,
  // suggestion chips, and lookup lists sometimes omit color — without this
  // branch they'd silently lose the smart indicator.)
  if (smart === true) {
    const hex = isPreset || isNil(color) || color === '' ? undefined : color;
    return (
      <span
        data-slot="tag-indicator"
        role="img"
        aria-label="Smart tag"
        className={cn('inline-flex items-center justify-center size-3 mx-0.5 min-w-3', className)}
        style={style}
        {...props}
      >
        <Zap
          className={cn('size-3', isPreset ? PRESET_ICON_COLORS[color as IndicatorColor] : undefined)}
          style={!isNil(hex) ? { color: hex, fill: hex } : undefined}
          aria-hidden
        />
      </span>
    );
  }

  // Emoji takes precedence over color — render as text inline so it follows font sizing.
  if (!isNil(emoji) && emoji !== '') {
    return (
      <span
        data-slot="tag-indicator"
        aria-hidden="true"
        className={cn('inline-flex size-3 shrink-0 items-center justify-center text-xs leading-none mx-0.5', className)}
        {...props}
      >
        {emoji}
      </span>
    );
  }

  if (isNil(color) || color === '') return null;

  return (
    <span
      data-slot="tag-indicator"
      className={cn(isPreset ? indicatorVariants({ color }) : 'size-2 rounded-full mx-0.5', className, 'min-w-2')}
      style={isPreset ? style : { backgroundColor: color, ...style }}
      {...props}
    />
  );
}

// ============================================================================
// TagDismiss
// ============================================================================

function TagDismiss({ className, ...props }: React.ComponentProps<'button'>) {
  return (
    <button
      type="button"
      data-slot="tag-dismiss"
      aria-label={'Remove'}
      className={cn(
        'ml-0.5 rounded-sm opacity-70 ring-offset-background transition-opacity',
        'hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-1',
        'disabled:pointer-events-none cursor-pointer',
        className,
      )}
      {...props}
    >
      <X className="size-3" />
    </button>
  );
}

// ============================================================================
// TagRoot
// ============================================================================

function TagRoot({ className, children, ...props }: React.ComponentProps<typeof Badge>) {
  return (
    <Badge variant="outline" className={cn('font-normal text-sm cursor-default normal-case', className)} {...props}>
      {children}
    </Badge>
  );
}

// ============================================================================
// Tag
// ============================================================================

type TagVariant = NonNullable<VariantProps<typeof tagVariants>['variant']>;

type TagProps = Omit<React.ComponentProps<typeof Badge>, 'variant' | 'type'> &
  VariantProps<typeof tagVariants> & {
    readonly dismissible?: boolean;
    readonly onDismiss?: () => void;
    readonly type?: 'filter' | 'selectable' | 'operational' | 'read-only' | 'interactive';
    readonly color?: IndicatorColor | string;
    readonly emoji?: string;
    readonly smart?: boolean;
    readonly labelClassName?: string;
  };

function Tag({
  className,
  variant,
  dismissible = false,
  onDismiss,
  children,
  type,
  color,
  emoji,
  smart,
  labelClassName,
  ...props
}: TagProps) {
  const effectiveVariant: TagVariant = variant ?? type ?? 'read-only';
  const isFocusable = effectiveVariant !== 'read-only';

  return (
    <Badge
      data-slot="tag"
      variant="outline"
      tabIndex={isFocusable ? 0 : undefined}
      className={cn(tagVariants({ variant: effectiveVariant }), className)}
      {...props}
    >
      <TagIndicator color={color} emoji={emoji} smart={smart} />
      <span data-slot="tag-label" className={labelClassName}>
        {children}
      </span>
      {dismissible && <TagDismiss onClick={onDismiss} />}
    </Badge>
  );
}

// ============================================================================
// Exports
// ============================================================================

export default Tag;
export { Tag, TagRoot, TagIndicator, TagDismiss, PRESET_COLORS, type IndicatorColor, type TagVariant };
